/**
 * LeadInboxPage
 *
 * Flat list of un-responded lead messages. Each row links to the lead detail
 * page with a scroll-to-message hash (#msg-<messageId>).
 *
 * Route: /leasing/inbox
 * US-9 — See un-responded inbound messages (cross-listing inbox)
 */

import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import type { BadgeProps } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { fetchInboxUnresponded, type UnrespondedMessage } from '../../lib/leasing.api';
import { useDomainEvents } from '../../hooks/useDomainEvents';
import { formatUnitAddress } from '@resida/shared';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatAge(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

// ---------------------------------------------------------------------------
// Page Component
// ---------------------------------------------------------------------------

export function LeadInboxPage() {
  const queryClient = useQueryClient();

  // DC-2/LS-BR-NOTIF-14 — all eight leasing DomainEvent arms can change what's unresponded,
  // plus case.message_received (JR-1): a new inbound message is what PUTS a lead onto this
  // list in the first place — without this key the list only caught up after staleTime (60s)
  // or a WebSocket reconnect, never live.
  const invalidateInbox = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['inbox-unresponded'] });
  }, [queryClient]);

  useDomainEvents({
    'case.message_received': invalidateInbox,
    'lead.decided': invalidateInbox,
    'appointment_proposal.created': invalidateInbox,
    'appointment_proposal.confirmed': invalidateInbox,
    'appointment_proposal.rejected': invalidateInbox,
    'appointment_proposal.cancelled': invalidateInbox,
    'appointment_proposal.rescheduled': invalidateInbox,
    'showing.scheduled': invalidateInbox,
    'applicant.submitted': invalidateInbox,
  });

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['inbox-unresponded'],
    queryFn: () => fetchInboxUnresponded(),
    staleTime: 60_000,
  });

  const items = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;

  return (
    <div data-testid="inbox-page" className="max-w-3xl mx-auto pb-6 px-[var(--page-padding-x)] pt-6">
      {/* Count summary — only shown when there are items */}
      {!isLoading && !error && items.length > 0 && (
        <p className="text-sm text-muted-foreground mb-4">
          {`${totalCount} lead${totalCount === 1 ? '' : 's'} awaiting a reply`}
        </p>
      )}

      {/* Error */}
      {error && (
        <div
          data-testid="inbox-error"
          className="p-3 mb-4 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm flex items-center justify-between gap-3"
          role="alert"
        >
          <span>{error instanceof Error ? error.message : 'Failed to load inbox'}</span>
          <Button variant="outline" size="sm" onClick={() => void refetch()} data-testid="inbox-retry">
            Retry
          </Button>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div data-testid="inbox-loading" className="space-y-3">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}
        </div>
      )}

      {/* Inbox list */}
      {!isLoading && !error && (
        <>
          {items.length === 0 ? (
            <div data-testid="inbox-empty" className="py-16 text-center text-muted-foreground">
              <p className="text-base font-medium mb-1">All caught up!</p>
              <p className="text-sm">No prospects are waiting for a reply.</p>
            </div>
          ) : (
            <ul
              data-testid="inbox-list"
              className="space-y-3"
              aria-label="Unresponded messages"
            >
              {items.map((item) => (
                <InboxRow
                  key={item.leadId}
                  item={item}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inbox Row
// ---------------------------------------------------------------------------

interface InboxRowProps {
  item: UnrespondedMessage;
}

const LEAD_STAGE_VARIANTS: Record<string, NonNullable<BadgeProps['variant']>> = {
  inquiring: 'info',
  showing: 'primary',
  applying: 'warning',
  verifying: 'warning',
  deciding: 'success',
  closed: 'secondary',
};

function InboxRow({ item }: InboxRowProps) {
  const stageVariant = LEAD_STAGE_VARIANTS[item.leadStage] ?? 'secondary';
  const stageLabel = item.leadStage.charAt(0).toUpperCase() + item.leadStage.slice(1);

  const addressDisplay = item.propertyAddress
    ? formatUnitAddress(item.propertyAddress, item.unitLabel, ' · ')
    : null;

  return (
    <li data-testid={`inbox-row-${item.leadId}`}>
      <Link
        to={`/leasing/leads/${item.leadId}`}
        aria-label={`Open lead for ${item.prospectName ?? item.prospectEmail}`}
        className="flex items-start justify-between gap-3 rounded-lg border border-border bg-card p-4 hover:bg-accent/50 transition-colors"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <p className="font-medium text-foreground truncate">
              {item.prospectName ?? item.prospectEmail}
            </p>
            <Badge
              data-testid={`inbox-row-stage-${item.leadId}`}
              variant={stageVariant}
            >
              {stageLabel}
            </Badge>
            {item.isNewInquiry ? (
              <Badge
                data-testid={`inbox-row-type-${item.leadId}`}
                variant="info"
              >
                New inquiry
              </Badge>
            ) : (
              <Badge
                data-testid={`inbox-row-type-${item.leadId}`}
                variant="secondary"
              >
                New message
              </Badge>
            )}
          </div>
          {item.prospectName && (
            <p className="text-xs text-muted-foreground">{item.prospectEmail}</p>
          )}
          {addressDisplay ? (
            <p
              data-testid={`inbox-row-address-${item.leadId}`}
              className="text-xs text-muted-foreground mt-0.5"
            >
              {addressDisplay}
            </p>
          ) : (
            <p
              data-testid={`inbox-row-address-${item.leadId}`}
              className="text-xs text-muted-foreground mt-0.5 italic"
            >
              Address not available
            </p>
          )}
          <div className="flex items-center gap-3 mt-1">
            <span className="text-xs text-muted-foreground">
              {item.inboundCount} message{item.inboundCount !== 1 ? 's' : ''} · Last {formatAge(item.lastInboundAt)}
            </span>
          </div>
        </div>
        {item.isNewInquiry && (
          <Badge
            data-testid={`inbox-needs-reply-badge-${item.leadId}`}
            variant="danger"
            className="shrink-0"
          >
            Needs Reply
          </Badge>
        )}
      </Link>
    </li>
  );
}
